/**
 * Roles and permissions (KAN-46).
 *
 * The permission matrix lives here, in shared code, so the API and the SPA
 * cannot drift apart on what a role may do. The SPA uses it to hide controls;
 * the API uses it to *enforce*. Hiding is not enforcement — every check in the
 * matrix is applied server-side by the bouncer policies.
 */
export const ROLES = ['finance', 'legal_production', 'executive'];
export const ROLE_LABELS = {
    finance: 'Finance',
    legal_production: 'Legal Production Team',
    executive: 'Executive',
};
/**
 * Permissions are coarse-grained and named after what the business calls them,
 * so the matrix can be read by a non-engineer during finance review.
 */
export const PERMISSIONS = [
    'firm_financials.view',
    'reports.view',
    'reports.edit_draft',
    'reports.distribute',
    'billing_exceptions.view_own_matters',
    'billing_exceptions.view_all',
    'billing_exceptions.resolve',
    'billing_forecast.generate',
    'cash_flow.view',
    'cash_flow.manage',
    'budgets.view',
    'budgets.manage',
    'ar.view',
    'ar.manage',
    'refunds.view',
    'refunds.manage',
    'trust_ledger.view',
    'alerts.view',
    'alerts.manage',
    'integrations.manage',
    'audit_log.view',
    'users.manage',
];
/**
 * KAN-46 acceptance criteria, encoded:
 *  - Finance may view and edit draft reports before distribution.
 *  - Legal Production may see billing exceptions for their matters, but NOT
 *    firm-wide financial dashboards.
 *  - Executive has read-only access to the consolidated dashboard.
 */
export const ROLE_PERMISSIONS = {
    finance: [
        'firm_financials.view',
        'reports.view',
        'reports.edit_draft',
        'reports.distribute',
        'billing_exceptions.view_all',
        'billing_exceptions.resolve',
        'billing_forecast.generate',
        'cash_flow.view',
        'cash_flow.manage',
        'budgets.view',
        'budgets.manage',
        'ar.view',
        'ar.manage',
        'refunds.view',
        'refunds.manage',
        'trust_ledger.view',
        'alerts.view',
        'alerts.manage',
        'integrations.manage',
        'audit_log.view',
        'users.manage',
    ],
    legal_production: ['billing_exceptions.view_own_matters', 'billing_exceptions.resolve'],
    /**
     * Executive reads the refund dashboard and decides nothing on it. That is not
     * an oversight about who ought to sign off a refund — it is the read-only
     * rule applied consistently. If the firm later wants approval to sit with
     * somebody who is not Finance, that is a change to this matrix and nothing
     * else; `refunds.manage` is already the only thing the write routes check.
     */
    /**
     * `alerts.view` and not `alerts.manage`: Executive reads the exception feed
     * and does not set the thresholds that produce it, which is the read-only
     * rule applied consistently rather than a claim about who cares about a
     * trust shortfall. Each alert additionally declares the grant its own subject
     * needs (`ALERT_META[code].permission`) and is withheld without it, so this
     * grant opens the page rather than the contents — the same two-tier shape the
     * executive dashboard uses for its tiles.
     */
    executive: [
        'firm_financials.view',
        'reports.view',
        'cash_flow.view',
        'budgets.view',
        'ar.view',
        'refunds.view',
        'alerts.view',
    ],
};
export function can(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
/** True when the role may only read. Executive is read-only by definition. */
export function isReadOnly(role) {
    return role === 'executive';
}
/**
 * Human-readable metadata for each permission.
 *
 * This lives beside the matrix rather than in the SPA because the roles screen
 * is meant to be read during finance review by someone who will never open the
 * codebase — and a label that drifts from the permission it describes is worse
 * than no label. Keeping both in one file means adding a permission without
 * describing it is a type error.
 */
export const PERMISSION_GROUPS = [
    'Dashboards',
    'Reporting',
    'Billing exceptions',
    'Refunds',
    'Alerts',
    'Administration',
];
export const PERMISSION_META = {
    'firm_financials.view': {
        label: 'View firm financials',
        description: 'The consolidated revenue, WIP and productivity dashboards.',
        group: 'Dashboards',
    },
    'ar.view': {
        label: 'View accounts receivable',
        description: 'Outstanding invoices by aging bucket, client, matter and attorney, with collection risk and the QuickBooks reconciliation.',
        group: 'Dashboards',
    },
    'cash_flow.view': {
        label: 'View cash flow',
        description: 'Cash position, upcoming obligations and the rolling 30/60/90-day forecast, with pending vendor bills ranked against projected cash.',
        group: 'Dashboards',
    },
    'budgets.view': {
        label: 'View budget vs. actual',
        description: 'The firm’s budget beside what QuickBooks says was actually earned and spent, by account and period, with the variance and which way is favourable. Forecast scenarios are read under the same grant.',
        group: 'Dashboards',
    },
    'budgets.manage': {
        label: 'Maintain budgets and scenarios',
        description: 'Import the budget, and create, adjust, lock and archive forecast scenarios. Held apart from the cash-flow grant because a firm may well let somebody set the budget without also setting the payroll schedule.',
        group: 'Administration',
    },
    'ar.manage': {
        label: 'Record payment commitments',
        description: 'Record what a client said they would pay and by when, and close a commitment as kept, missed or withdrawn. A commitment is never netted against the balance — it is evidence about a conversation, not about money.',
        group: 'Administration',
    },
    'cash_flow.manage': {
        label: 'Maintain recurring obligations',
        description: 'Add, amend and end-date the payroll, tax and other scheduled outflows the cash forecast is built from. Changes take effect without a deployment. Nothing here pays anything.',
        group: 'Administration',
    },
    'trust_ledger.view': {
        label: 'View trust ledger',
        description: 'IOLTA client trust balances and the append-only movement history.',
        group: 'Dashboards',
    },
    'reports.view': {
        label: 'View reports',
        description: 'Read finalised and draft reporting packs.',
        group: 'Reporting',
    },
    'reports.edit_draft': {
        label: 'Edit draft reports',
        description: 'Amend a reporting pack before it is distributed.',
        group: 'Reporting',
    },
    'reports.distribute': {
        label: 'Distribute reports',
        description: 'Release a report to its recipients. Irreversible.',
        group: 'Reporting',
    },
    'billing_exceptions.view_own_matters': {
        label: 'View own matter exceptions',
        description: 'Billing exceptions limited to matters this person is responsible for.',
        group: 'Billing exceptions',
    },
    'billing_exceptions.view_all': {
        label: 'View all exceptions',
        description: 'Billing exceptions across every matter in the firm.',
        group: 'Billing exceptions',
    },
    'billing_exceptions.resolve': {
        label: 'Resolve exceptions',
        description: 'Clear or annotate an exception. Scoped by whichever view grant applies.',
        group: 'Billing exceptions',
    },
    'refunds.view': {
        label: 'View refunds',
        description: 'Money returned to clients: amount, reason, approver and status, the trend by client and attorney, and the invoice or trust movement behind each one.',
        group: 'Refunds',
    },
    'refunds.manage': {
        label: 'Record and decide refunds',
        description: 'Record a refund for approval, approve or decline one, and classify why a refund that arrived from Clio happened.',
        group: 'Refunds',
    },
    'alerts.view': {
        label: 'View exception alerts',
        description: 'The feed of thresholds that have been crossed, and the history of how long each took to resolve. Each alert is additionally withheld from anyone lacking the grant for the data behind it.',
        group: 'Alerts',
    },
    'alerts.manage': {
        label: 'Configure alert thresholds',
        description: 'Turn alert rules on or off, set the value each one trips at, and decide who is notified. Takes effect without a deployment.',
        group: 'Alerts',
    },
    'billing_forecast.generate': {
        label: 'Regenerate the billing forecast',
        description: 'Recompute the billing-cycle projection from current data, ahead of its automatic refresh.',
        group: 'Reporting',
    },
    'integrations.manage': {
        label: 'Manage integrations',
        description: 'Connect Clio and QuickBooks, and trigger a sync out of schedule.',
        group: 'Administration',
    },
    'audit_log.view': {
        label: 'View audit log',
        description: 'The append-only record of every change to financial data.',
        group: 'Administration',
    },
    'users.manage': {
        label: 'Manage users',
        description: 'Invite people, change roles, and deactivate accounts.',
        group: 'Administration',
    },
};
/** Permissions belonging to one group, in the order they are declared above. */
export function permissionsInGroup(group) {
    return PERMISSIONS.filter((permission) => PERMISSION_META[permission].group === group);
}
//# sourceMappingURL=roles.js.map